import 'moment/locale/zh-tw';

import {
  Button,
  Card,
  DatePicker,
  Radio,
  Select,
  Space,
  Typography,
} from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import { Timetable } from '@/components/UI/Timetable';
import type {
  ProgramBlock,
  ScheduleType,
} from '@/components/UI/Timetable/types';

import {
  DUMMY_SCHEDULE_DATA,
  ProgramStatusColors,
  SELECT_DROPDOWN_STYLES,
  TEACHER_OPTIONS,
} from './constants';
import { displaySelectedWeek } from './helpers';
import styles from './index.module.scss';

moment.locale('zh-tw');
moment.updateLocale('zh-tw', {
  week: {
    dow: 1,
    doy: 4,
  },
});

const { Text } = Typography;

export const Schedule = () => {
  const [scheduleType, setScheduleType] =
    useState<ScheduleType>('columnSchedule');
  const [isDataLoading, setIsDataLoading] = useState(false); // TODO: 測試用state，到時候改用真實資料時，再改用swr的isLoading

  const [selectedDate, setSelectedDate] = useState(moment());
  const [selectedWeek, setSelectedWeek] = useState(moment());
  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
  const [weekPickerOpen, setWeekPickerOpen] = useState(false);

  const availableTeachers = TEACHER_OPTIONS.map((option) => option.value);

  const displayColumnHeaders =
    scheduleType === 'columnSchedule' ? availableTeachers : [];

  // TODO: 測試loading效果用，最後開發完時記得移除
  useEffect(() => {
    if (isDataLoading) {
      const timer = setTimeout(() => {
        setIsDataLoading(false);
        clearTimeout(timer);
      }, 1000);
    }
  }, [isDataLoading]);

  const handleScheduleTypeChange = (newType: ScheduleType) => {
    setIsDataLoading(true);
    if (newType === 'columnSchedule') {
      setSelectedWeek(moment());
    } else {
      setSelectedDate(moment());
    }
    setScheduleType(newType);
  };

  const handleThisWeekClick = () => {
    setSelectedWeek(moment());
    setWeekPickerOpen(false);
  };

  const scheduleData = DUMMY_SCHEDULE_DATA.map((block) => {
    const { status, ...rest } = block;

    const scheduleBlock: ProgramBlock = {
      ...rest,
      backgroundColor: ProgramStatusColors[status],
    };
    return scheduleBlock;
  });

  return (
    <Space direction="vertical" size="middle" className="w-full">
      <Space size="middle">
        <Text>表格形式：</Text>
        <Radio.Group
          value={scheduleType}
          onChange={(e) => handleScheduleTypeChange(e.target.value)}
          disabled={isDataLoading}
        >
          <Radio value="columnSchedule">講師排表</Radio>
          <Radio value="weekSchedule">時間排表</Radio>
        </Radio.Group>
      </Space>

      <Card loading={isDataLoading} className="w-full">
        <Space direction="vertical" size="middle" className="w-full">
          <Space>
            {scheduleType === 'columnSchedule' ? (
              <Space align="baseline" size={0}>
                <Text>日期：</Text>
                <DatePicker
                  allowClear={false}
                  placeholder="請選擇日期"
                  className={styles.filter}
                  value={selectedDate}
                  onChange={(date) => !!date && setSelectedDate(date)}
                  popupClassName={styles.popup}
                />
              </Space>
            ) : (
              <Space>
                <Space align="baseline" size={0}>
                  <Text>日期：</Text>
                  <DatePicker
                    picker="week"
                    allowClear={false}
                    placeholder="請選擇週"
                    value={selectedWeek}
                    onChange={(date) => !!date && setSelectedWeek(date)}
                    format={displaySelectedWeek}
                    popupClassName={styles.popup}
                    open={weekPickerOpen}
                    onOpenChange={setWeekPickerOpen}
                    renderExtraFooter={() => (
                      <Button
                        type="link"
                        size="small"
                        className="w-full"
                        onClick={handleThisWeekClick}
                      >
                        本週
                      </Button>
                    )}
                  />
                </Space>

                <Space align="baseline" size={0}>
                  <Text>講師：</Text>
                  <Select
                    allowClear
                    className={styles.filter}
                    placeholder="請選擇講師"
                    options={TEACHER_OPTIONS}
                    value={selectedTeacher}
                    onChange={(teacher) => setSelectedTeacher(teacher)}
                    dropdownStyle={SELECT_DROPDOWN_STYLES}
                  />
                </Space>
              </Space>
            )}
          </Space>

          <div className="w-full">
            <ul className={styles.colorDefinition}>
              {Object.entries(ProgramStatusColors).map(
                ([statusText, backgroundColor], index) => {
                  return (
                    <li key={index}>
                      <div
                        className={styles.colorCircle}
                        style={{ backgroundColor }}
                      />
                      <p>{statusText}</p>
                    </li>
                  );
                }
              )}
            </ul>

            <div className={styles.timetableWrapper}>
              <Timetable
                showInstructor={scheduleType === 'weekSchedule'}
                scheduleData={scheduleData}
                scheduleType={scheduleType}
                columnHeaders={displayColumnHeaders}
                columnField="instructor"
              />
            </div>
          </div>
        </Space>
      </Card>
    </Space>
  );
};
